import { useContext } from "react";
import { SandboxContext } from "./SandboxContext";

// Use the main sandbox context. Useful if you need to know the overall status of the sandbox and access the available APIs/URLs.
export const useSandbox = () => useContext(SandboxContext);

// Access filesystem and editor state from the sandbox context
export const useFilesystem = () => {
  const {
    currentLanguage,
    filesystem,
    editor,
    fileLookup,
    beginEditingFile,
    handleEditorChange,
    saveCurrentFile,
  } = useContext(SandboxContext);
  return {
    currentLanguage,
    filesystem,
    editor,
    fileLookup,
    beginEditingFile,
    handleEditorChange,
    saveCurrentFile,
  };
};

// Access the list of submissions for the current sandbox/lesson
export const useSubmissionList = () => {
  const { submissions } = useContext(SandboxContext);
  return submissions;
};

// Access AI hints and related state from the sandbox context
export const useAiHints = () => {
  const {
    aiHintsEnabled,
    hints,
    hintsLoading,
    hintsError,
    highlightedHint,
    setHighlightedHint,
    generateAiHint,
    provideHintFeedback,
  } = useContext(SandboxContext);
  return {
    aiHintsEnabled,
    hints,
    hintsLoading,
    hintsError,
    highlightedHint,
    setHighlightedHint,
    generateAiHint,
    provideHintFeedback,
  };
};

// Access testing state and methods from the sandbox context
export const useTesting = () => {
  const { hasTests, isTestRunning, runTests, resetSandbox, testRequestError, testStatus } =
    useContext(SandboxContext);
  return { hasTests, isTestRunning, runTests, resetSandbox, testRequestError, testStatus };
};

// Access proxy intercept state and methods from the sandbox context
export const useProxyIntercept = () => {
  const { isIntercepting, pendingRequestText, setInterceptState, resumeInterceptedRequest } =
    useContext(SandboxContext);
  return {
    isIntercepting,
    pendingRequestText,
    setInterceptState,
    resumeInterceptedRequest,
  };
};

// Access proxy request history from the sandbox context
export const useProxyRequests = () => {
  const { requests } = useContext(SandboxContext);
  return { requests };
};

// Access sandbox output lines from the sandbox context
export const useSandboxOutput = () => {
  const { outputLines } = useContext(SandboxContext);
  return { outputLines };
};

// Access session tracking state and methods from the sandbox context
export const useSession = () => {
  const { session, acquireSandbox, resetSandbox } = useContext(SandboxContext);
  return {
    session,
    acquireSandbox,
    resetSandbox,
  };
};

// Access interaction reporting methods from the sandbox context
export const useInteraction = () => {
  const {
    maxStepCompleted,
    reportStepsCompleted,
    reportAllStepsCompleted,
    useHint,
    submitCtfProof,
  } = useContext(SandboxContext);
  return {
    maxStepCompleted,
    reportStepsCompleted,
    reportAllStepsCompleted,
    useHint,
    submitCtfProof,
  };
};

// Hook for instruction and task UI
export const useInstructionDetails = () => {
  const { lessonDetails } = useContext(SandboxContext);
  if (!lessonDetails) {
    return { instructions: null, tasks: null };
  }

  return {
    hasFinishButton: lessonDetails.hasFinishButton,
    instructions: lessonDetails.sandbox.instructions,
  };
};
